import { useEffect } from 'react';
import { CheckoutSession } from '../../redux/slices/subscriptionSlice';
import toast from 'react-hot-toast';

interface PaystackCheckoutLauncherProps {
  session: CheckoutSession | null;
  onSuccess: () => void;
  onClose: () => void;
}

const PAYSTACK_SCRIPT_ID = 'paystack-inline-js';
let paystackScriptPromise: Promise<void> | null = null;

const loadPaystackScript = () => {
  if ((window as any).PaystackPop) return Promise.resolve();
  if (paystackScriptPromise) return paystackScriptPromise;

  paystackScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(PAYSTACK_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing || document.createElement('script');
    script.id = PAYSTACK_SCRIPT_ID;
    script.src = 'https://js.paystack.co/v2/inline.js';
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => {
      paystackScriptPromise = null;
      script.remove();
      reject(new Error('Could not load Paystack checkout'));
    }, { once: true });
    if (!existing) document.body.appendChild(script);
  });

  return paystackScriptPromise;
};

const PaystackCheckoutLauncher = ({ session, onSuccess, onClose }: PaystackCheckoutLauncherProps) => {
  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    void loadPaystackScript().then(() => {
      if (cancelled) return;
      const PaystackPop = (window as any).PaystackPop;
      if (!PaystackPop || !session.accessCode) {
        toast.error('Unable to open checkout. Please try again.');
        onClose();
        return;
      }
      try {
        const popup = new PaystackPop();
        popup.resumeTransaction(session.accessCode, {
          onSuccess: () => {
            toast('Payment submitted. Verifying...');
            onSuccess();
          },
          onCancel: () => {
            toast('Payment window closed');
            onClose();
          },
          onError: () => {
            toast.error('Unable to complete checkout. Please try again.');
            onClose();
          },
        });
      } catch {
        toast.error('Unable to open checkout. Please try again.');
        onClose();
      }
    }).catch(() => {
      if (cancelled) return;
      toast.error('Unable to open checkout. Please try again.');
      onClose();
    });

    return () => {
      cancelled = true;
    };
  }, [session, onSuccess, onClose]);

  return null;
};

export default PaystackCheckoutLauncher;
